function escapeInlineMarkdown(text: string): string {
  return text.replace(/([\\`*_[\]~])/g, '\\$1');
}

function formatLinkUrl(url: string): string {
  const encoded = url.replace(/\|/g, '%7C');
  if (/[\s<>()]/.test(encoded)) {
    return `<${encoded.replace(/[<>]/g, '')}>`;
  }
  return encoded;
}

function wrapInline(inner: string, marker: string): string {
  const trimmed = inner.trim();
  if (!trimmed) return inner;
  const leading = inner.match(/^\s*/)?.[0] || '';
  const trailing = inner.match(/\s*$/)?.[0] || '';
  return `${leading}${marker}${trimmed}${marker}${trailing}`;
}

function nodeToCellMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeInlineMarkdown(node.textContent || '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const element = node as Element;
  const tag = element.tagName;

  if (tag === 'A') {
    const href = (element as HTMLAnchorElement).getAttribute('href') || '';
    const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    if (href && text)
      return `[${escapeInlineMarkdown(text)}](${formatLinkUrl(href)})`;
    if (text) return escapeInlineMarkdown(text);
    if (href) return href;
    return '';
  }

  if (tag === 'BR') return ' ';

  if (tag === 'CODE') {
    const raw = element.textContent || '';
    if (!raw.trim()) return '';
    if (raw.includes('`')) return `\`\` ${raw} \`\``;
    return `\`${raw}\``;
  }

  let inner = '';
  element.childNodes.forEach((child) => {
    inner += nodeToCellMarkdown(child);
  });

  if (tag === 'STRONG' || tag === 'B') return wrapInline(inner, '**');
  if (tag === 'EM' || tag === 'I') return wrapInline(inner, '*');
  if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE')
    return wrapInline(inner, '~~');

  return inner;
}

function normalizeCell(cell: Element): string {
  let raw = '';
  cell.childNodes.forEach((node) => {
    raw += nodeToCellMarkdown(node);
  });
  return raw
    .replace(/\r?\n/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\|/g, '\\|')
    .trim();
}

function convertHtmlTableToGfm(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'));
  if (rows.length === 0) return '';

  const matrix = rows.map((row) =>
    Array.from(row.querySelectorAll('th, td')).map(normalizeCell)
  );

  const colCount = matrix.reduce((max, row) => Math.max(max, row.length), 0);
  if (colCount === 0) return '';

  const normalized = matrix.map((row) => {
    const padded = row.slice();
    while (padded.length < colCount) padded.push('');
    return padded;
  });

  const firstRowIsHeader = rows[0].querySelector('th') !== null;

  const header = firstRowIsHeader
    ? normalized[0]
    : Array.from({ length: colCount }, () => '');
  const body = firstRowIsHeader ? normalized.slice(1) : normalized;

  const headerLine = `| ${header.join(' | ')} |`;
  const separatorLine = `| ${header.map(() => '---').join(' | ')} |`;
  const bodyLines = body.map((row) => `| ${row.join(' | ')} |`);

  return [headerLine, separatorLine, ...bodyLines].join('\n');
}

export default convertHtmlTableToGfm;
